import { AuthBindings } from "@refinedev/core";
import { SmartApi } from "../../api";
import { getErrorMessage } from "../../api/error";
import jwt_decode, { JwtDecodeOptions } from "jwt-decode";
import { LoginApiProps } from "../../api/interfaces";
import { setPermission } from "../../store/use-permission";

export enum Role {
  USER = "USER",
  ADMIN = "ADMIN",
}

interface IDecodedToken {
  token: string;
  options?: JwtDecodeOptions | string;
  id: number;
  email: string;
  role: Role;
  userId: string;
}

export interface IIdentity {
  email?: string;
  role?: Role;
}

export interface IUserDetails {
  id: number;
  email: string;
  fullName: string;
  userId: string;
  smartUID: string;
}

export const TOKEN_KEY = "refine-auth";
export const REFRESH_KEY = "refresh-auth";
export const USER_DETAILS_KEY = "refine-user-details";
/** Onboarding status from /profile or login details; used to restrict new orders when RESTRICTED */
export const ONBOARDING_STATUS_KEY = "onboarding_status";

export type SmartAuthBindings = AuthBindings & {
  resendVerifyEmail: (email: string) => Promise<any>;
};

export function makeAuthProvider(rootUrl: string): SmartAuthBindings {
  const api = new SmartApi(rootUrl);
  return {
    forgotPassword: async ({ email }) => {
      try {
        await api.forgotPassword({ email });
        return {
          success: true,
          redirectTo: "/",
        };
      } catch (err) {
        const message = getErrorMessage(err);
        return {
          success: false,
          error: {
            name: "Error resetting password",
            message,
          },
        };
      }
    },
    register: async ({
      email,
      password,
      address,
      fullName,
      userId,
      website,
      regNo,
      countryOfIncorporation,
      directorName,
    }) => {
      try {
        const { data: registrationData } = await api.register({
          email,
          password,
          address,
          fullName,
          userId,
          website,
          regNo,
          countryOfIncorporation,
          directorName,
        });
        const { isUserVerified, accountType } = registrationData;

        if (!isUserVerified) {
          return {
            success: true,
            redirectTo: "/confirm-email",
          };
        }

        const { data } = await api.login({
          email,
          password,
          isAdminLogin: false,
        });
        localStorage.setItem(TOKEN_KEY, data["access_token"]);
        localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(data["details"]));

        if (accountType === "BETA") {
          return {
            success: true,
            redirectTo: "/wallet",
          };
        }

        return {
          success: true,
          redirectTo: "/",
        };
      } catch (err) {
        const message = getErrorMessage(err);
        return {
          success: false,
          error: {
            name: "Error creating account",
            message,
          },
        };
      }
    },
    updatePassword: async ({ password, code }) => {
      if (code === null) {
        return {
          success: false,
          error: {
            name: "ResetPasswordError",
            message:
              'The reset password link has either expired or become invalid. You can submit a new request on the "Forgot your password?" page.',
          },
        };
      }

      try {
        await api.resetPassword({ password, code });
        return {
          success: true,
          redirectTo: "/login",
        };
      } catch (err) {
        const message = getErrorMessage(err);
        return {
          success: false,
          error: {
            name: "Error resetting password",
            message,
          },
        };
      }
    },
    login: async ({ email, password, isAdminLogin }: LoginApiProps) => {
      try {
        const { data } = await api.login({
          email,
          password,
          isAdminLogin,
        });
        localStorage.setItem(TOKEN_KEY, data["access_token"]);
        localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(data["details"]));
        return {
          success: true,
          redirectTo: "/",
        };
      } catch (err) {
        const message = getErrorMessage(err);
        return {
          success: false,
          error: {
            name: "Login error",
            message,
          },
        };
      }
    },
    logout: async () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_DETAILS_KEY);
      localStorage.removeItem(ONBOARDING_STATUS_KEY);
      return {
        success: true,
        redirectTo: "/login",
      };
    },
    check: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const details = localStorage.getItem(USER_DETAILS_KEY);
      if (token && details) {
        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        redirectTo: "/login",
      };
    },
    getPermissions: async () => null,
    getIdentity: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        const decodedToken = jwt_decode<IDecodedToken>(token);
        return {
          id: decodedToken.id,
          email: decodedToken.email,
          role: decodedToken.role,
          userId: decodedToken.userId,
        };
      }
      return null;
    },
    onError: async (error) => {
      if (error.request.status === 401) {
        setPermission(false);
        // return {
        //   logout: true,
        //   redirectTo: "/login",
        // };
      }
      return { error };
    },
    resendVerifyEmail: async (email) => {
      if (email.length === 0) {
        return {
          success: false,
          error: {
            name: "Error verifying email",
            message: "The email confirmation link has become invalid",
          },
        };
      }
      try {
        await api.resendVerifyEmail(email);
        return {
          success: true,
        };
      } catch (err) {
        const message = getErrorMessage(err);
        return {
          success: false,
          error: {
            name: "Error verifying email",
            message,
          },
        };
      }
    },
  };
}
